import { ImageAnnotationType } from '@/modules/types/pages/imageReview/imageAnnotationType'
import { debounce, DebouncedFunc } from 'lodash'

type UpdateFunctionType = (annotation: ImageAnnotationType) => void

interface DebounceItem {
  update: DebouncedFunc<() => void>
  state: ImageAnnotationType | null
}

export class DebounceManager {
  items: Record<string, DebounceItem>
  updateFunction: UpdateFunctionType

  constructor(updateFunction: UpdateFunctionType) {
    this.items = {}
    this.updateFunction = updateFunction
  }

  /**
   * Handles creating debounced function for a specicific marker update. Updates the state to the most recent state passed in.
   * @param state ImageAnnotationType
   */
  onMarkerInteraction(state: ImageAnnotationType) {
    // Capture the state at the time of interaction
    const elementState = state

    if (!this.items[state.annotationUuid]) {
      // Create a new debounced function for new elements
      this.items[state.annotationUuid] = {
        update: debounce(this.processUpdate.bind(this, state.annotationUuid), 1000),
        state: null,
      }
    }

    // Update the state
    this.items[state.annotationUuid].state = elementState

    // Call the debounced function
    this.items[state.annotationUuid].update()
  }

  /**
   * Processes the update with the corresponding annotation uuid
   * @param uuid of the annotation
   */
  processUpdate(annotationUuid: string) {
    // Safety check: ensure the item still exists (in case clear() was called mid-flight)
    const item = this.items[annotationUuid]
    if (item && item.state) {
      this.updateFunction(item.state)
    }
  }

  /**
   * Cancels all pending updates and releases memory.
   * Call this when switching images or unmounting the view.
   */
  clear() {
    Object.values(this.items).forEach((item) => {
      // Lodash debounce returns a function with a .cancel method
      // This prevents the update from firing if the user has already left the image
      item.update.cancel()
    })
    this.items = {}
  }
}
